import React from "react";

import { useMutation, useQuery } from "@apollo/client";
import { ExpandMore, PushPinOutlined } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  timelineItemClasses,
  TimelineSeparator,
} from "@mui/lab";
import { Avatar, Box, Drawer, MenuItem, Select, Skeleton } from "@mui/material";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import { ContainedButton, IconButton } from "@/components/Button";
import { ContainedIconButton } from "@/components/Button/ContainedIconButton";
import { OutlinedIconButton } from "@/components/Button/OutlinedIconButton";
import { Status } from "@/components/Chip/Status";
import { BodyHyperlink } from "@/components/Hyperlinks/BodyHyperlink";
import pinnedClients from "@/components/Sidebar/ClickComponents/Client/store/PinnedClients";
import { Spacer } from "@/components/Spacer/Spacer";
import { HeadlineBold, Label, SubheadBold } from "@/components/Typography";
import { ClientsPageClientFragment, TrackingStatus } from "@/gql/graphql";
import { useDefaultStyles } from "@/hooks/useDefaultStyles";
import useDialogState from "@/hooks/useDialogState";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { formatISODateTime } from "@/utils/datetime";
import { displayError } from "@/utils/errors";

import RemarkForm, { RemarkValues } from "../ClientList/components/RemarkForm";
import TimelineEntry from "../ClientList/components/TimelineEntry";
import {
  createEventRemarkMutation,
  updateClientTrackingStatusMutation,
} from "../graphql/clientsPageMutations";
import { clientTimelineQuery } from "../graphql/clientsPageQueries";

const useStyles = makeStyles()({
  timelineContainer: {
    border: "1px solid #DEE6F0",
    marginTop: 12,
    overflowY: "auto",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 100,
  },
  timeline: {
    padding: "0px 20px",
    m: 0,
    [`& .${timelineItemClasses.root}:before`]: {
      flex: 0,
      padding: 0,
    },
  },
  timelineDot: {
    marginBottom: 0,
  },
  timelineContent: {
    paddingRight: 0,
  },
});

const trackingStatusChip = {
  [TrackingStatus.Active]: <Status label="Active" />,
  [TrackingStatus.Cancelled]: <Status color="secondary" label="Cancelled" />,
  [TrackingStatus.NoAction]: <Status color="error" label="No Action" />,
  [TrackingStatus.Onboarding]: <Status label="Onboarding" />,
  [TrackingStatus.Pending]: <Status color="warning" label="Pending" />,
  [TrackingStatus.TestOnly]: <Status color="secondary" label="Test only" />,
};

interface ClientTimelineActivityDrawerProps {
  isOpen: boolean;
  client: ClientsPageClientFragment;
  onClose: () => void;
}

const ClientTimelineActivityDrawer: React.FC<ClientTimelineActivityDrawerProps> =
  observer(({ isOpen, client, onClose }) => {
    const { classes } = useStyles();
    const { classes: defaultClasses } = useDefaultStyles();
    const snackbar = useGlobalSnackbar();
    const newRemarkForm = useDialogState();

    // API
    const { data, loading } = useQuery(clientTimelineQuery, {
      variables: { id: client.id },
    });
    const [createEventRemark] = useMutation(createEventRemarkMutation);
    const [_updateTrackingStatus] = useMutation(
      updateClientTrackingStatusMutation
    );
    const createRemark = async (values: RemarkValues) => {
      try {
        await createEventRemark({
          variables: {
            input: {
              comment: values.comment,
              createdAt: formatISODateTime(values.createdAt),
              clientId: client.id,
            },
            clientId: client.id,
          },
        });
        newRemarkForm.hide();
        snackbar.open("Remark successfully added.");
      } catch (e) {
        snackbar.open(displayError(e, "Unable to save remark."));
      }
    };

    // Functions
    const updateTrackingStatus = async (status: TrackingStatus) => {
      try {
        _updateTrackingStatus({
          variables: { input: { clientId: client.id, trackingStatus: status } },
          optimisticResponse: {
            __typename: "Mutation",
            updateTrackingStatus: {
              __typename: "UpdateTrackingStatusPayload",
              client: {
                __typename: "Client",
                id: client.id,
                trackingStatus: status,
              },
            },
          },
        });
      } catch (e) {
        snackbar.open(displayError(e, "Unable to update status."));
      }
    };

    const renderLoadingActivities = (
      <>
        <Box>
          <Skeleton width="70%" />
          <Spacer h={4} />
          <Skeleton width="40%" />
        </Box>
      </>
    );

    return (
      <Drawer open={isOpen} onClose={onClose} anchor="right">
        <Box p={5} width="450px">
          <Box display="flex" alignItems="flex-end" justifyContent="flex-end">
            <IconButton
              icon={<CloseIcon fontSize="small" />}
              size="small"
              onClick={onClose}
            />
          </Box>
          <Spacer h={16} />
          <Box display="flex" alignItems="center">
            <Box display="flex" alignItems="center" flex={1}>
              <Avatar src={client?.profile?.avatarUrl ?? undefined} />
              <Spacer w={16} />
              <Box>
                <BodyHyperlink text={client?.profile?.fullName} />
                <SubheadBold
                  text={client?.email}
                  className={defaultClasses.textWrap}
                  maxWidth="280px"
                />
                <SubheadBold text={client?.profile?.mobileNumber} />
              </Box>
            </Box>
            {pinnedClients.has(client.id) ? (
              <ContainedIconButton
                icon={
                  <PushPinOutlined
                    onClick={() => pinnedClients.toggle(client.id)}
                    fontSize="small"
                  />
                }
                size="smaller"
                onClick={() => pinnedClients.toggle(client.id)}
              />
            ) : (
              <OutlinedIconButton
                icon={
                  <PushPinOutlined
                    onClick={() => pinnedClients.toggle(client.id)}
                    fontSize="small"
                  />
                }
                size="smaller"
                onClick={() => pinnedClients.toggle(client.id)}
              />
            )}
          </Box>
          <Spacer h={24} />
          <Label text="Status" />
          <Spacer h={8} />
          <Select
            variant="standard"
            IconComponent={() => (
              <ExpandMore
                className={defaultClasses.cursorPointer}
                color="primary"
                fontSize="small"
              />
            )}
            fullWidth
            value={client.trackingStatus}
            onChange={(e) => {
              updateTrackingStatus(e.target.value as TrackingStatus);
            }}
          >
            {Object.values(TrackingStatus).map((status) => (
              <MenuItem key={status} value={status}>
                <React.Fragment key={status}>
                  {trackingStatusChip[status]}
                </React.Fragment>
              </MenuItem>
            ))}
          </Select>
          <Spacer h={24} />
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <HeadlineBold text="Timeline" />
            <ContainedButton
              onClick={newRemarkForm.show}
              startIcon={<AddIcon />}
              size="small"
              text="Add Remarks"
            />
          </Box>
          <Spacer h={12} />
          <Box>
            <Box
              border={`1px solid ${colors.mapped.borderBreaks.neutral.naturalLight}`}
              overflow="auto"
            >
              {loading && !data ? (
                <Box pl="46px" py="32px">
                  {Array.from({ length: 10 }).map((_, index) => (
                    <React.Fragment key={index}>
                      {renderLoadingActivities}
                      {index < 9 && <Spacer h={32} />}{" "}
                      {/* Add Spacer only between items */}
                    </React.Fragment>
                  ))}
                </Box>
              ) : (
                <Timeline className={classes.timeline}>
                  {newRemarkForm.shown && (
                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot className={classes.timelineDot} />
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent className={classes.timelineContent}>
                        <RemarkForm
                          onCancel={newRemarkForm.hide}
                          onSubmit={createRemark}
                        />
                      </TimelineContent>
                    </TimelineItem>
                  )}
                  {data?.client?.events.nodes.map((event, i) => (
                    <TimelineEntry
                      key={event.id}
                      eventId={event.id}
                      clientId={client.id}
                      isFirst={!newRemarkForm.shown && i === 0}
                      isLast={i + 1 === data.client?.events.nodes.length}
                    />
                  ))}
                </Timeline>
              )}
            </Box>
          </Box>
        </Box>
      </Drawer>
    );
  });

export default ClientTimelineActivityDrawer;
